import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock, LogOut, ChevronDown, X, Briefcase, CalendarDays } from "lucide-react";
import { fmtDateUS } from "@/lib/api";
import { Time12Input, DateUSInput } from "@/components/inputs";
import { ScheduleCalendar } from "@/components/ScheduleCalendar";
import { ForemanProjectCard } from "@/components/ForemanProjectCard";

const API = process.env.REACT_APP_BACKEND_URL;

const inputCls = "rounded-lg w-full bg-white/5 border border-white/15 px-4 py-4 text-base text-white placeholder:text-white/35 focus:outline-none focus:border-primary transition-colors duration-300";
const selectCls = `${inputCls} appearance-none`;

const useCollapse = (key, def = false) => {
  // crew preference: sections always start folded, no persistence
  const [open, setOpen] = useState(def);
  const toggle = () => setOpen((o) => !o);
  return [open, toggle];
};

const empFetch = async (path, options = {}) => {
  const token = localStorage.getItem("is_emp_token");
  const res = await fetch(`${API}/api${path}`, {
    ...options,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(options.headers || {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401) {
      localStorage.removeItem("is_emp_token");
      localStorage.removeItem("is_emp");
      window.location.reload();
    }
    throw new Error(typeof data.detail === "string" ? data.detail : "Something went wrong");
  }
  return data;
};

const today = () => new Date().toISOString().slice(0, 10);

const fmtTime12 = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ampm}`;
};

export default function TimeClock() {
  const [emp, setEmp] = useState(null);
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const [jobs, setJobs] = useState([]);
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState({ job_id: "", note: "" });
  const [dayRows, setDayRows] = useState([{ date: today(), start: "", end: "" }]);
  const [workerSel, setWorkerSel] = useState([]);
  const [areaSel, setAreaSel] = useState([]); // work areas (tracked items) these hours went to
  const [workers, setWorkers] = useState([]);
  const [addingWorker, setAddingWorker] = useState(false);
  const [newWorkerName, setNewWorkerName] = useState("");
  const [customRows, setCustomRows] = useState({}); // workerId -> own day rows (default: same as foreman's)
  const [editingCustom, setEditingCustom] = useState(null);
  const [logOpen, toggleLog] = useCollapse("time-collapse-log", false);
  const [projOpen, toggleProj] = useCollapse("time-collapse-projects", false);
  const [busy, setBusy] = useState(false);
  const canForeman = !!emp && (emp.role === "foreman" || !!emp.can_foreman);

  const load = useCallback(async () => {
    try {
      const [j, e] = await Promise.all([empFetch("/time/jobs"), empFetch("/time/entries")]);
      setJobs(j);
      setEntries(e);
      if (j.length && !form.job_id) setForm((f) => ({ ...f, job_id: j[0].id }));
      const me = JSON.parse(localStorage.getItem("is_emp") || "{}");
      if (me.role === "foreman" || me.can_foreman) {
        const w = await empFetch("/time/workers").catch(() => []);
        setWorkers(w);
        setWorkerSel((ws) => (ws.length ? ws : [me.id])); // "Me" preselected — untap if you didn't work
      }
    } catch (err) {
      toast.error(err.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (emp) load(); }, [emp, load]);

  // Always start at the login screen — no remembered sessions (shared crew phones)
  useEffect(() => {
    localStorage.removeItem("is_emp_token");
    localStorage.removeItem("is_emp");
  }, []);

  const login = async () => {
    if (!name.trim()) return toast.error("Enter your name");
    if (!pin.trim()) return toast.error("Enter your PIN");
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/time/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin: pin.trim(), name: name.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Login failed");
      localStorage.setItem("is_emp_token", data.token);
      localStorage.setItem("is_emp", JSON.stringify(data.employee));
      setEmp(data.employee);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("is_emp_token");
    localStorage.removeItem("is_emp");
    setEmp(null);
    setName("");
    setPin("");
  };

  const setRow = (i, patch) => setDayRows((rs) => rs.map((r, x) => (x === i ? { ...r, ...patch } : r)));
  const toggleWorker = (id) => setWorkerSel((ws) => (ws.includes(id) ? ws.filter((w) => w !== id) : [...ws, id]));

  const addWorker = async () => {
    if (newWorkerName.trim().length < 2) return toast.error("Enter the worker's name");
    setBusy(true);
    try {
      const w = await empFetch("/time/workers", { method: "POST", body: JSON.stringify({ name: newWorkerName.trim() }) });
      setWorkers((ws) => [...ws.filter((x) => x.id !== w.id), w]);
      setWorkerSel((ws) => (ws.includes(w.id) ? ws : [...ws, w.id]));
      setNewWorkerName("");
      setAddingWorker(false);
      toast.success(w.already_existed ? `${w.name} was already on the crew — selected` : `${w.name} added — the office sets their wage`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const startCustom = (w) => {
    setCustomRows((cr) => ({ ...cr, [w.id]: cr[w.id] || dayRows.map((r) => ({ ...r })) }));
    setEditingCustom(w.id);
  };
  const clearCustom = (id) => {
    setCustomRows((cr) => { const n = { ...cr }; delete n[id]; return n; });
    setEditingCustom((e) => (e === id ? null : e));
  };
  const setCustomRow = (id, i, patch) =>
    setCustomRows((cr) => ({ ...cr, [id]: cr[id].map((r, x) => (x === i ? { ...r, ...patch } : r)) }));

  const validRows = (rows) => rows.filter((r) => r.date && r.start && r.end);

  const logHours = async () => {
    if (!form.job_id) return toast.error("Pick a project");
    const clean = validRows(dayRows);
    if (clean.length === 0) return toast.error("Add at least one day with start and end times");
    for (const r of clean) {
      if (r.end <= r.start) return toast.error(`On ${r.date} the end time must be after the start time`);
    }
    const plain = workerSel.filter((id) => !customRows[id]);
    const custom = workerSel.filter((id) => customRows[id]);
    for (const id of custom) {
      const w = workers.find((x) => x.id === id);
      const rows = validRows(customRows[id]);
      if (!rows.length) return toast.error(`${w?.name || "Worker"}: custom hours are empty — fill them in or switch back to "same as mine"`);
      for (const r of rows) {
        if (r.end <= r.start) return toast.error(`${w?.name || "Worker"}: on ${r.date} the end time must be after the start time`);
      }
    }
    setBusy(true);
    try {
      let created = 0;
      if (plain.length) {
        const res = await empFetch("/time/entries/batch", {
          method: "POST",
          body: JSON.stringify({
            job_id: form.job_id, note: form.note, worker_ids: plain, item_ids: areaSel,
            rows: clean.map((r) => ({ date: r.date, start_time: r.start, end_time: r.end })),
          }),
        });
        created += res.created;
      }
      for (const id of custom) {
        const res = await empFetch("/time/entries/batch", {
          method: "POST",
          body: JSON.stringify({
            job_id: form.job_id, note: form.note, worker_ids: [id], item_ids: areaSel,
            rows: validRows(customRows[id]).map((r) => ({ date: r.date, start_time: r.start, end_time: r.end })),
          }),
        });
        created += res.created;
      }
      toast.success(`${created} ${created === 1 ? "entry" : "entries"} logged — thank you`);
      setDayRows([{ date: today(), start: "", end: "" }]);
      setWorkerSel([emp.id]);
      setCustomRows({});
      setEditingCustom(null);
      setForm((f) => ({ ...f, note: "" }));
      setAreaSel([]);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!emp) {
    return (
      <div className="min-h-screen bg-background grain flex items-center justify-center px-5" data-testid="time-login">
        <div className="w-full max-w-sm border border-white/10 bg-card rounded-2xl p-8 text-center">
          <img src="/logo.png" alt="Infinite Siding LLC" className="h-12 w-auto logo-on-dark mx-auto mb-6" />
          <h1 className="font-display text-4xl tracking-wide">CREW TIME</h1>
          <p className="text-white/45 text-xs mt-2 mb-6">Enter your name and PIN to log hours</p>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name — e.g. Luis"
            data-testid="time-name-input"
            autoCapitalize="words"
            className={`${inputCls} text-center mb-3`}
          />
          <input
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
            onKeyDown={(e) => e.key === "Enter" && login()}
            type="password"
            inputMode="numeric"
            placeholder="Your PIN"
            data-testid="time-pin-input"
            className={`${inputCls} text-center text-2xl tracking-[0.5em] font-bold`}
          />
          <button onClick={login} disabled={busy} data-testid="time-login-button" className="mt-4 w-full bg-primary text-primary-foreground font-bold uppercase tracking-widest text-sm py-4 rounded-lg disabled:opacity-50">
            {busy ? "Checking..." : "Enter"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background grain pb-16" data-testid="time-app">
      <header className="sticky top-0 z-40 bg-[#171914]/85 backdrop-blur-xl border-b border-white/10 px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Infinite Siding LLC" className="h-8 w-auto logo-on-dark" />
          <div>
            <p className="font-bold text-sm" data-testid="time-emp-name">{emp.name}</p>
            <p className="text-[10px] uppercase tracking-widest text-primary font-bold">{emp.role === "foreman" ? "foreman" : emp.can_foreman ? "crew lead" : emp.role}</p>
          </div>
        </div>
        <button onClick={logout} data-testid="time-logout" className="flex items-center gap-2 text-white/50 hover:text-red-300 text-xs font-bold uppercase tracking-widest transition-colors duration-300">
          <LogOut size={14} aria-hidden="true" /> Out
        </button>
      </header>

      <main className="max-w-lg mx-auto px-5 pt-8 space-y-8">
        <section className="border border-white/10 rounded-2xl bg-card p-5 space-y-3" data-testid="time-log-form">
          <button type="button" onClick={toggleLog} data-testid="time-log-toggle" aria-expanded={logOpen} className="flex items-center justify-between w-full text-left group">
            <h2 className="font-display text-2xl tracking-wide flex items-center gap-2 group-hover:text-primary transition-colors duration-300"><Clock size={18} className="text-primary" aria-hidden="true" /> LOG HOURS</h2>
            <ChevronDown size={18} className={`text-white/40 group-hover:text-primary transition-transform duration-300 ${logOpen ? "rotate-180" : ""}`} aria-hidden="true" />
          </button>
          {logOpen && (<>
          <p className="text-[11px] text-white/40">Add as many days as you need — hours are counted from your start and end times.</p>
          <div className="relative">
            <select value={form.job_id} onChange={(e) => setForm({ ...form, job_id: e.target.value })} data-testid="time-job-select" className={selectCls}>
              {jobs.length === 0 && <option value="" className="bg-[#252720]">No open projects right now</option>}
              {jobs.map((j) => <option key={j.id} value={j.id} className="bg-[#252720]">{j.client_name} — {j.title}</option>)}
            </select>
            <ChevronDown size={15} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" aria-hidden="true" />
          </div>
          {(() => {
            const selJob = jobs.find((j) => j.id === form.job_id);
            const items = selJob?.progress?.items || [];
            if (!items.length) return null;
            return (
              <div data-testid="time-area-chips" className="space-y-1.5">
                <p className="text-[10px] font-bold uppercase tracking-widest text-white/40">Areas worked on (optional) — the office splits these hours across your picks</p>
                <div className="flex flex-wrap gap-1.5">
                  {items.map((it) => {
                    const on = areaSel.includes(it.id);
                    return (
                      <button key={it.id} type="button" onClick={() => setAreaSel((s) => (on ? s.filter((x) => x !== it.id) : [...s, it.id]))} data-testid={`time-area-chip-${it.id}`}
                              className={`px-2.5 py-1.5 rounded-full text-[11px] font-bold border transition-colors duration-200 ${on ? "bg-primary text-primary-foreground border-primary" : "border-white/20 text-white/60 hover:border-primary hover:text-primary"}`}>
                        {it.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          {canForeman && (
            <div data-testid="time-worker-chips" className="space-y-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-white/40">Who worked? — tap everyone these hours are for (your crew first)</p>
              <div className="flex flex-wrap gap-2">
                {workers.map((w) => {
                  const on = workerSel.includes(w.id);
                  return (
                    <button key={w.id} type="button" onClick={() => toggleWorker(w.id)} data-testid={`time-worker-chip-${w.id}`} className={`px-3.5 py-2 rounded-full text-xs font-bold border transition-colors duration-200 ${on ? "bg-primary text-primary-foreground border-primary" : "border-white/20 text-white/60 hover:border-primary hover:text-primary"}`}>
                      {w.id === emp.id ? `Me (${emp.name})` : w.name}
                    </button>
                  );
                })}
                {addingWorker ? (
                  <div className="flex gap-2 w-full" data-testid="time-new-worker-form">
                    <input value={newWorkerName} onChange={(e) => setNewWorkerName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addWorker()}
                           placeholder="New crew member's name" data-testid="time-new-worker-name" autoCapitalize="words" className={`${inputCls} !py-2.5 !text-sm`} />
                    <button type="button" onClick={addWorker} disabled={busy} data-testid="time-new-worker-add" className="shrink-0 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-[11px] px-4 rounded-lg disabled:opacity-50">Add</button>
                    <button type="button" onClick={() => { setAddingWorker(false); setNewWorkerName(""); }} aria-label="Cancel" data-testid="time-new-worker-cancel" className="shrink-0 text-white/40 hover:text-white/70 px-1"><X size={15} /></button>
                  </div>
                ) : (
                  <button type="button" onClick={() => setAddingWorker(true)} data-testid="time-new-worker-open" className="px-3.5 py-2 rounded-full text-xs font-bold border border-dashed border-white/25 text-white/60 hover:border-primary hover:text-primary transition-colors duration-200">
                    + New crew member
                  </button>
                )}
              </div>
              <p className="text-[10px] text-white/35">{workerSel.length === 0 ? "Nobody picked — hours go to you" : `${workerSel.length} ${workerSel.length === 1 ? "person gets" : "people get"} these hours`} · new names get a wage from the office</p>
              {workerSel.filter((id) => id !== emp.id).map((id) => {
                const w = workers.find((x) => x.id === id);
                if (!w) return null;
                const custom = customRows[id];
                return (
                  <div key={id} className="border border-white/10 rounded-lg bg-background/60 px-3 py-2 space-y-2" data-testid={`time-crew-copy-${id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[11px] text-white/60"><span className="font-semibold text-white/85">{w.name}</span> — {custom ? "own times" : "same times as you"}</p>
                      <button type="button" onClick={() => (custom ? clearCustom(id) : startCustom(w))} data-testid={`time-crew-adjust-${id}`} className="text-[10px] font-bold uppercase tracking-widest text-primary hover:underline shrink-0">
                        {custom ? "Back to mine" : "Adjust"}
                      </button>
                    </div>
                    {custom && editingCustom === id && (
                      <div className="space-y-1.5" data-testid={`time-custom-${id}`}>
                        {custom.map((r, i) => (
                          <div key={i} className="space-y-1.5 border border-white/10 rounded-lg p-2">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-[10px] text-white/50 whitespace-nowrap">{fmtDateUS(r.date)}</span>
                              {custom.length > 1 && (
                                <button type="button" onClick={() => setCustomRows((cr) => ({ ...cr, [id]: cr[id].filter((_, x) => x !== i) }))} aria-label="Remove day" data-testid={`time-custom-remove-${id}-${i}`} className="text-red-400/70 hover:text-red-300 p-0.5 shrink-0"><X size={13} /></button>
                              )}
                            </div>
                            <Time12Input value={r.start} onChange={(v) => setCustomRow(id, i, { start: v })} testid={`time-custom-start-${id}-${i}`} />
                            <Time12Input value={r.end} onChange={(v) => setCustomRow(id, i, { end: v })} testid={`time-custom-end-${id}-${i}`} />
                          </div>
                        ))}
                        <button type="button" onClick={() => setEditingCustom(null)} data-testid={`time-custom-done-${id}`} className="text-[10px] font-bold uppercase tracking-widest text-white/50 hover:text-primary">Done</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <div className="space-y-2">
            {dayRows.map((r, i) => (
              <div key={i} className="border border-white/10 rounded-lg bg-background/60 p-2.5 space-y-2" data-testid={`time-day-row-${i}`}>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <DateUSInput value={r.date} onChange={(v) => setRow(i, { date: v })} testid={`time-day-date-${i}`} />
                  </div>
                  {dayRows.length > 1 && (
                    <button type="button" onClick={() => setDayRows((rs) => rs.filter((_, x) => x !== i))} aria-label="Remove day" data-testid={`time-day-remove-${i}`} className="text-red-400/70 hover:text-red-300 p-1.5 shrink-0"><X size={14} /></button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[9px] font-bold uppercase tracking-widest text-white/40 block mb-1">Start</label>
                    <Time12Input value={r.start} onChange={(v) => setRow(i, { start: v })} testid={`time-day-start-${i}`} />
                  </div>
                  <div>
                    <label className="text-[9px] font-bold uppercase tracking-widest text-white/40 block mb-1">End</label>
                    <Time12Input value={r.end} onChange={(v) => setRow(i, { end: v })} testid={`time-day-end-${i}`} />
                  </div>
                </div>
              </div>
            ))}
            {dayRows.length < 14 && (
              <button type="button" onClick={() => setDayRows((rs) => [...rs, { date: today(), start: "", end: "" }])} data-testid="time-day-add" className="w-full border border-dashed border-white/25 rounded-lg py-3 text-[11px] font-bold uppercase tracking-widest text-white/60 hover:border-primary hover:text-primary transition-colors duration-300">
                + Add another day
              </button>
            )}
          </div>
          <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="What did you work on? (optional)" data-testid="time-note-input" className={inputCls} />
          <button onClick={logHours} disabled={busy || !jobs.length} data-testid="time-submit-button" className="w-full bg-primary text-primary-foreground font-bold uppercase tracking-widest text-sm py-4 rounded-lg disabled:opacity-50">
            {(() => { const c = dayRows.filter((r) => r.date && r.start && r.end).length; return busy ? "Saving..." : c > 0 ? `Log ${c} day${c === 1 ? "" : "s"}` : "Log Hours"; })()}
          </button>
          </>)}
        </section>

        {canForeman && jobs.filter((j) => j.assigned_foreman_id === emp.id).length > 0 && (
          <section className="space-y-3" data-testid="time-projects">
            <button type="button" onClick={toggleProj} data-testid="time-projects-toggle" aria-expanded={projOpen} className="flex items-center justify-between w-full text-left group">
              <h2 className="font-display text-2xl tracking-wide flex items-center gap-2 group-hover:text-primary transition-colors duration-300"><Briefcase size={18} className="text-primary" aria-hidden="true" /> MY PROJECTS</h2>
              <ChevronDown size={18} className={`text-white/40 group-hover:text-primary transition-transform duration-300 ${projOpen ? "rotate-180" : ""}`} aria-hidden="true" />
            </button>
            {projOpen && jobs.filter((j) => j.assigned_foreman_id === emp.id).map((j) => (
              <ForemanProjectCard key={j.id} job={j} emp={emp} empFetch={empFetch} onChanged={load} />
            ))}
          </section>
        )}

        <section className="space-y-3" data-testid="time-calendar-section">
          <h2 className="font-display text-2xl tracking-wide flex items-center gap-2"><CalendarDays size={18} className="text-primary" aria-hidden="true" /> MY CALENDAR</h2>
          <ScheduleCalendar mode="viewer" empFetch={empFetch} />
        </section>

        <section data-testid="time-recent">
          <h2 className="font-display text-2xl tracking-wide mb-3">MY RECENT HOURS</h2>
          {entries.length === 0 && <p className="text-white/40 text-sm" data-testid="time-no-entries">No hours logged yet.</p>}
          <div className="space-y-2">
            {entries.map((e) => (
              <div key={e.id} className="border border-white/10 rounded-xl bg-card px-4 py-3" data-testid={`time-entry-${e.id}`}>
                <p className="text-sm font-semibold truncate">{jobs.find((j) => j.id === e.job_id)?.title || "Project"}{e.status === "pending" ? <span className="text-[9px] font-bold uppercase tracking-widest text-amber-300 ml-2" data-testid={`time-entry-status-${e.id}`}>pending review</span> : null}</p>
                <p className="text-[11px] text-white/45">{e.employee_name} · {fmtDateUS(e.date)}{e.start_time ? ` · ${fmtTime12(e.start_time)} – ${fmtTime12(e.end_time)}` : ""}{e.note ? ` · ${e.note}` : ""}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
